import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { DictionariesApiService } from '@lib/dictionaries/core/services/dictionaries-api.service';
import { EMPTY, Observable, catchError, debounceTime, expand, filter, forkJoin, map, of, reduce, tap, throwError } from 'rxjs';

import { withCappedEntry } from '../utils/capped-entry.utils';
import {
  ICD10_BLOCK_LEVEL,
  ICD10_CATEGORY_LEVEL,
  ICD10_SEARCH_MIN_LENGTH,
  Icd10BlockGroup,
  Icd10DictionaryEntry,
  Icd10NamesLoadState,
  icd10BlockPrefixes,
  icd10CategoryPrefixes,
  icd10DisplayName,
  icd10LeafEntries,
  isIcd10CodeUnder,
} from '../utils/icd10-diagnosis-tree.utils';

/** Key of the block list among the loaded nodes; never evicted from the cache. */
export const ICD10_BLOCKS_KEY = '__blocks__';

const SEARCH_PAGE_SIZE = 500;
/** Pages followed when expanding a group — enough for the largest МКХ-10 block. */
const EXPAND_MAX_PAGES = 20;
const NODES_CACHE_LIMIT = 50;
const SEARCHES_CACHE_LIMIT = 10;

export interface Icd10NodeNames extends Icd10NamesLoadState {
  names: Record<string, string>;
}

export interface Icd10SearchState extends Icd10NamesLoadState {
  /** Matching entries, keyed by code → display name. */
  results: Record<string, string>;
  entries: Icd10DictionaryEntry[];
}

/**
 * Root cache of the МКХ-10 dictionary as diagnoses trees read it — the block list, names per opened block or
 * category, and search results, each with its own load state; fetched once, reused, and capped. Also expands a
 * block or category into the lowest-level diagnoses under it, which is what a coverage option stores.
 *
 * Shared by every app that shows a coverage's diagnoses: the dictionary is not tied to a member or a package.
 */
@Injectable({ providedIn: 'root' })
export class Icd10DictionaryStore {
  private readonly dictionaries = inject(DictionariesApiService);

  private readonly nodesSignal = signal<Record<string, Icd10NodeNames>>({});
  private readonly searchesSignal = signal<Record<string, Icd10SearchState>>({});
  /** Names learned while expanding groups, so freshly added codes read by name at once. */
  private readonly expandedNamesSignal = signal<Record<string, string>>({});

  /** Load state and names per tree node, keyed by block key or category (and `ICD10_BLOCKS_KEY`). */
  readonly nodes = this.nodesSignal.asReadonly();
  readonly searches = this.searchesSignal.asReadonly();

  readonly blocksState = computed(() => this.nodesSignal()[ICD10_BLOCKS_KEY]);
  /** Block codes (`O94-O99`) a tree groups categories under. */
  readonly blockCodes = computed(() => Object.keys(this.blocksState()?.names ?? {}));

  /** Every name known so far, keyed by code — blocks, categories and diagnoses alike. */
  readonly names = computed<Record<string, string>>(() =>
    Object.assign(
      {},
      this.expandedNamesSignal(),
      ...Object.values(this.searchesSignal()).map(search => search.results),
      ...Object.values(this.nodesSignal()).map(node => node.names)
    )
  );

  loadBlocks(): void {
    this.loadNode(ICD10_BLOCKS_KEY, [''], ICD10_BLOCK_LEVEL);
  }

  /** Names of a block's categories, asked by their short prefixes (see `icd10CategoryPrefixes`). */
  loadBlockNames(group: Icd10BlockGroup): void {
    this.loadNode(group.key, icd10CategoryPrefixes(group.categories.map(item => item.category)), ICD10_CATEGORY_LEVEL);
  }

  loadCategoryNames(category: string): void {
    this.loadNode(category, [category]);
  }

  /** Entries matching a term — one page, which is what a person reads. Asked once per term unless it failed. */
  search(term: string): void {
    const current = this.searchesSignal()[term];
    if (current && !current.error) return;

    this.setSearch(term, { loading: true, error: false, results: {}, entries: [] });
    this.searchPages(term, undefined, 1)
      .pipe(catchError(() => of(null)))
      .subscribe(entries =>
        this.setSearch(term, entries ? { loading: false, error: false, results: toNames(entries), entries } : { loading: false, error: true, results: {}, entries: [] })
      );
  }

  /**
   * The lowest-level diagnoses under a block, category or diagnosis, across every page of the dictionary. A block
   * is asked by the prefixes of its range and cut back to it; a class is too broad to take as a whole and is refused.
   */
  expand(entry: Icd10DictionaryEntry): Observable<Icd10DictionaryEntry[]> {
    if (entry.level < ICD10_BLOCK_LEVEL) return throwError(() => new Error('An МКХ-10 class cannot be expanded as a whole'));

    const terms = entry.level === ICD10_BLOCK_LEVEL ? icd10BlockPrefixes(entry.code) : [entry.code];

    return this.searchAll(terms, undefined, EXPAND_MAX_PAGES).pipe(
      map(found => {
        const leaves = icd10LeafEntries(found.filter(item => isIcd10CodeUnder(item.code, entry)));
        return leaves.length || entry.level < ICD10_CATEGORY_LEVEL ? leaves : icd10LeafEntries([entry]);
      }),
      tap(leaves => this.expandedNamesSignal.update(names => ({ ...names, ...toNames(leaves) })))
    );
  }

  private loadNode(key: string, searchTerms: string[], level?: number): void {
    const current = this.nodesSignal()[key];
    if (current && !current.error) return;

    this.setNode(key, { loading: true, error: false, names: {} });
    this.searchAll(searchTerms, level, 1)
      .pipe(
        map(toNames),
        catchError(() => of(null))
      )
      .subscribe(names => this.setNode(key, names ? { loading: false, error: false, names } : { loading: false, error: true, names: {} }));
  }

  /** Every entry for any of the terms; no terms — nothing to ask, so an empty answer rather than one that never comes. */
  private searchAll(terms: string[], level: number | undefined, maxPages: number): Observable<Icd10DictionaryEntry[]> {
    return terms.length ? forkJoin(terms.map(term => this.searchPages(term, level, maxPages))).pipe(map(pages => pages.flat())) : of([]);
  }

  /** Every entry for a term, following continuation tokens for at most `maxPages` pages. */
  private searchPages(searchTerm: string, level: number | undefined, maxPages: number): Observable<Icd10DictionaryEntry[]> {
    const page = (continuationToken?: string) =>
      this.dictionaries.searchIcd10Diagnoses({ searchTerm, level, continuationToken, pageSize: SEARCH_PAGE_SIZE, sortBy: 'code', sortDirection: 'Asc' });

    return page().pipe(
      expand((response, index) => (response.continuationToken && index + 1 < maxPages ? page(response.continuationToken) : EMPTY)),
      reduce<{ items: Icd10DictionaryEntry[] }, Icd10DictionaryEntry[]>((entries, response) => [...entries, ...response.items], [])
    );
  }

  private setNode(key: string, state: Icd10NodeNames): void {
    this.nodesSignal.update(nodes => withCappedEntry(nodes, key, state, NODES_CACHE_LIMIT, ICD10_BLOCKS_KEY));
  }

  private setSearch(term: string, state: Icd10SearchState): void {
    this.searchesSignal.update(searches => withCappedEntry(searches, term, state, SEARCHES_CACHE_LIMIT));
  }
}

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Searches the dictionary as a term signal changes — debounced, from `ICD10_SEARCH_MIN_LENGTH` characters — and
 * returns that term's search state. Call in an injection context; the subscription ends with it.
 */
export function injectIcd10Search(term: Signal<string>): Signal<Icd10SearchState | null> {
  const store = inject(Icd10DictionaryStore);
  const key = computed(() => term().trim());

  toObservable(key)
    .pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      filter(value => value.length >= ICD10_SEARCH_MIN_LENGTH),
      takeUntilDestroyed()
    )
    .subscribe(value => store.search(value));

  return computed(() => store.searches()[key()] ?? null);
}

function toNames(entries: Icd10DictionaryEntry[]): Record<string, string> {
  return Object.fromEntries(entries.map(entry => [entry.code.trim().toUpperCase(), icd10DisplayName(entry)]));
}
